import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
    MsalProvider,
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
} from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { LoginPage } from './pages/LoginPage';
import { AuthCallback } from './pages/AuthCallback';
import { Dashboard } from './pages/Dashboard';
import { Opportunities } from './pages/Opportunities';
import { Proposals } from './pages/Proposals';

import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Chargée à la demande : la plupart des utilisateurs ne sont pas admin et ne
// visitent jamais cette page, pas besoin d'en payer le poids dans le bundle
// initial que tout le monde télécharge pour juste voir la liste des avant-ventes.
const Admin = lazy(() => import('./pages/Admin').then((m) => ({ default: m.Admin })));

function PageLoader() {
    return (
        <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
        </div>
    );
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 1000,
            retry: 1,
        },
    },
});

interface AppProps {
    instance: PublicClientApplication;
}

// Microsoft ayant validé la connexion ne veut pas dire que notre backend a
// accepté l'échange de token (cf. isAuthenticated/authError ci-dessous) — sans
// cette garde, l'app affichait l'interface comme si tout allait bien pendant
// que chaque appel API échouait silencieusement en 401.
function AuthenticatedRoutes() {
    const { isLoading, isAuthenticated, authError, logout, user } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
                <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
                <div className="card max-w-sm w-full text-center">
                    <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-light)] rounded-lg px-3 py-2 mb-4">
                        {authError || "La connexion au serveur a échoué."}
                    </p>
                    <button onClick={logout} className="btn-secondary w-full">
                        Retourner à l'écran de connexion
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Layout>
            <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/opportunities" element={<Opportunities />} />
                {/* La page des propositions porte l'avant-vente dans son chemin
                    et la proposition ouverte en paramètre : un lien envoyé à un
                    collègue ouvre donc exactement ce qu'on regarde. */}
                <Route
                    path="/opportunities/:opportunityId/proposals"
                    element={<Proposals />}
                />
                {/* Ecran réservé aux admins : le lien de nav est déjà masqué pour les
                    autres (cf. Layout.tsx), mais la route doit aussi se garder
                    elle-même contre un accès direct par URL. */}
                <Route path="/admin" element={
                    user?.role === 'admin' ? (
                        <Suspense fallback={<PageLoader />}>
                            <Admin />
                        </Suspense>
                    ) : (
                        <Navigate to="/" replace />
                    )
                } />
                <Route path="/auth/callback" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function UnauthenticatedRoutes() {
    return (
        <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="*" element={<LoginPage />} />
        </Routes>
    );
}

function App({ instance }: AppProps) {
    return (
        <ThemeProvider>
            <QueryClientProvider client={queryClient}>
                <MsalProvider instance={instance}>
                    <AuthProvider>
                        <BrowserRouter>
                            <AuthenticatedTemplate>
                                <AuthenticatedRoutes />
                            </AuthenticatedTemplate>
                            <UnauthenticatedTemplate>
                                <UnauthenticatedRoutes />
                            </UnauthenticatedTemplate>
                        </BrowserRouter>
                    </AuthProvider>
                </MsalProvider>
            </QueryClientProvider>
        </ThemeProvider>
    );
}

export default App;
